"use client";

import { sendLoginOtp, verifyLoginOtp, loginUser, type LoginKind } from "@/features/auth/api/login";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { showError } from "@/lib/error-toast";

import { ROUTES } from "@/config/routes";
import { SYSTEM_ROLES } from "@/config/roles";

export type LoginStep = "credentials" | "otp" | "campaign_selection";

const TOAST_SEND_OTP_ID = "login-send-otp";
const TOAST_VERIFY_OTP_ID = "login-verify-otp";

// Resolve target dashboard route based on user role
function resolvePostLoginPath(user: unknown) {
  const isSuper =
    (user as any)?.isSuperAdmin ||
    (user as any)?.role === SYSTEM_ROLES.SUPER_ADMIN ||
    (user as any)?.role === "super_admin" ||
    (user as any)?.roleDefinition?.roleId === SYSTEM_ROLES.SUPER_ADMIN;

  if (isSuper) {
    return ROUTES.DASHBOARD_ADMIN;
  }
  return ROUTES.DASHBOARD;
}

function getTabId() {
  if (typeof window === "undefined") return "";
  let tabId = sessionStorage.getItem("crm_tab_id");
  if (!tabId) {
    tabId = Math.random().toString(36).substring(2, 10) + "_" + Date.now().toString(36);
    sessionStorage.setItem("crm_tab_id", tabId);
  }
  return tabId;
}

export function useOtpLogin(kind: LoginKind) {
  const router = useRouter();

  const [step, setStep] = useState<LoginStep>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [isForceLoggingIn, setIsForceLoggingIn] = useState(false);
  const [campaigns, setCampaigns] = useState<{ id: number; name: string }[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);

  async function fetchCampaignsAndProceed(user: any) {
    try {
      const campRes = await fetch("/api/v1/tatatele/dialer/campaigns");
      if (campRes.ok) {
        const campData = await campRes.json();
        const list = Array.isArray(campData) ? campData : (campData.data || []);
        if (Array.isArray(list) && list.length > 0) {
          setCampaigns(list);
          const savedId = localStorage.getItem("smartfloActiveCampaign");
          const defaultId = savedId && list.some((c: any) => c.id === parseInt(savedId))
            ? parseInt(savedId)
            : list[0].id;
          setSelectedCampaignId(defaultId);
          setPendingUser(user);
          setStep("campaign_selection");
          return;
        }
      }
    } catch (e) {
      console.warn("[Login] Failed to fetch campaigns during login:", e);
    }
    window.location.href = resolvePostLoginPath(user);
  }

  // Handle post-login redirection and optional telephony checks
  async function checkAndProceedLogin(user: any) {
    try {
      sessionStorage.removeItem("smartflo_stay_offline");
      sessionStorage.removeItem("smartflo_dialer_token");
      sessionStorage.removeItem("smartflo_session_id");
      sessionStorage.removeItem("smartflo_takeover_authorized");
      sessionStorage.setItem("crm_tab_session_active", Date.now().toString());
      sessionStorage.setItem("crm_last_active_timestamp", Date.now().toString());
    } catch {}

    const isSuper =
      kind === "admin" ||
      Boolean(user?.isSuperAdmin) ||
      user?.role === SYSTEM_ROLES.SUPER_ADMIN ||
      user?.role === "super_admin" ||
      user?.roleDefinition?.roleId === SYSTEM_ROLES.SUPER_ADMIN ||
      user?.roleDefinition?.name?.toLowerCase().includes("super");

    if (isSuper) {
      try {
        localStorage.removeItem("smartfloActiveCampaign");
        localStorage.removeItem("smartfloCampaigns");
      } catch {}
      window.location.href = resolvePostLoginPath(user);
      return;
    }

    try {
      const tabId = getTabId();
      const tokenRes = await fetch(`/api/v1/tatatele/dialer/token?tabId=${encodeURIComponent(tabId)}`);
      if (tokenRes.ok) {
        const tokenData = await tokenRes.json();
        if (tokenData.conflict) {
          toast.dismiss(TOAST_VERIFY_OTP_ID);
          setPendingUser(user);
          setShowConflictModal(true);
          return;
        }
      }
    } catch (e) {
      console.warn("[Login] Pre-redirect Smartflo session check skipped:", e);
    }
    await fetchCampaignsAndProceed(user);
  }

  const sendOtpMutation = useMutation({
    mutationFn: () => sendLoginOtp(kind, { email, password }),
    onMutate: () => {
      toast.loading("Sending OTP...", { id: TOAST_SEND_OTP_ID });
    },
    onSuccess: (result) => {
      if (result.email) {
        setEmail(result.email);
      }

      setStep("otp");
      toast.success(result.message || "OTP sent to your email", { id: TOAST_SEND_OTP_ID });
    },
    onError: (error: Error) => {
      showError(error, "Failed to send OTP", { id: TOAST_SEND_OTP_ID });
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: () => verifyLoginOtp(kind, { email, otp }),
    onMutate: () => {
      toast.loading("Verifying OTP...", { id: TOAST_VERIFY_OTP_ID });
    },
    onSuccess: (user) => {
      toast.success("Login successful", { id: TOAST_VERIFY_OTP_ID });
      checkAndProceedLogin(user);
    },
    onError: (error: Error) => {
      showError(error, "OTP verification failed", { id: TOAST_VERIFY_OTP_ID });
    },
  });

  const loginMutation = useMutation({
    mutationFn: () => loginUser({ email, password }),
    onMutate: () => {
      toast.loading("Signing in...", { id: TOAST_VERIFY_OTP_ID });
    },
    onSuccess: (user) => {
      toast.success("Login successful", { id: TOAST_VERIFY_OTP_ID });
      checkAndProceedLogin(user);
    },
    onError: (error: Error) => {
      showError(error, "Login failed", { id: TOAST_VERIFY_OTP_ID });
    },
  });

  useEffect(() => {
    setStep("credentials");
    setOtp("");
    sendOtpMutation.reset();
    verifyOtpMutation.reset();
    loginMutation.reset();
  }, [kind]);

  function handleCredentialsSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (kind === "user") {
      loginMutation.mutate();
    } else {
      sendOtpMutation.mutate();
    }
  }

  function handleOtpSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    verifyOtpMutation.mutate();
  }

  function handleBack() {
    setStep("credentials");
    setOtp("");
    sendOtpMutation.reset();
    verifyOtpMutation.reset();
    loginMutation.reset();
  }

  async function handleConfirmTakeover() {
    setIsForceLoggingIn(true);
    try {
      const tabId = getTabId();
      sessionStorage.setItem("smartflo_takeover_authorized", "true");
      await fetch(`/api/v1/tatatele/dialer/token?force=true&tabId=${encodeURIComponent(tabId)}`);
      sessionStorage.removeItem("smartflo_stay_offline");
      setShowConflictModal(false);
      await fetchCampaignsAndProceed(pendingUser);
    } catch (e) {
      console.error("[Login] Force takeover error:", e);
      toast.error("Failed to take over session. Please try again.");
    } finally {
      setIsForceLoggingIn(false);
    }
  }

  function handleCancelTakeover() {
    sessionStorage.setItem("smartflo_stay_offline", "true");
    setShowConflictModal(false);
    window.location.href = resolvePostLoginPath(pendingUser);
  }

  function handleProceedToDashboard() {
    if (selectedCampaignId) {
      localStorage.setItem("smartfloActiveCampaign", selectedCampaignId.toString());
    }
    if (campaigns.length > 0) {
      localStorage.setItem("smartfloCampaigns", JSON.stringify(campaigns));
    }
    window.location.href = resolvePostLoginPath(pendingUser);
  }

  const isSubmitting =
    sendOtpMutation.isPending || verifyOtpMutation.isPending || loginMutation.isPending || isForceLoggingIn;

  return {
    step,
    email,
    password,
    otp,
    showPassword,
    isSubmitting,
    isSendingOtp: kind === "user" ? loginMutation.isPending : sendOtpMutation.isPending,
    isVerifyingOtp: verifyOtpMutation.isPending,
    showConflictModal,
    isForceLoggingIn,
    campaigns,
    selectedCampaignId,
    pendingUser,
    setSelectedCampaignId,
    handleProceedToDashboard,
    handleConfirmTakeover,
    handleCancelTakeover,
    setEmail,
    setPassword,
    setOtp,
    setShowPassword,
    handleCredentialsSubmit,
    handleOtpSubmit,
    handleBack,
  };
}
